/**
 * Signal Trace mode: find listening posts, triangulate signal, extract.
 * Pure functions — no side effects, no rendering.
 */

import type { PlanetState, RuinState, Vec3, ModeConfig } from '../types';
import { DEFAULT_POST_ACTIVATION_TURNS } from '../constants';
import { dcos, dsin } from './dmath';

/**
 * Choose a signal planet and surface location.
 */
export function initSignalTrace(
  planets: PlanetState[],
  rng: { int: (min: number, max: number) => number; range: (min: number, max: number) => number }
): { signalPlanetIndex: number; signalSurfaceDir: Vec3 } {
  // Only pick from actual planets (not stars/wormholes)
  const spawnablePlanets = planets
    .map((p, i) => ({ planet: p, index: i }))
    .filter(e => e.planet.type === 'planet');

  if (spawnablePlanets.length === 0) {
    return { signalPlanetIndex: 0, signalSurfaceDir: { x: 0, y: 1, z: 0 } };
  }

  const chosen = spawnablePlanets[rng.int(0, spawnablePlanets.length - 1)];

  const theta = rng.range(0, Math.PI * 2);
  const phi = rng.range(0.3, Math.PI - 0.3);
  const surfaceDir: Vec3 = {
    x: dsin(phi) * dcos(theta),
    y: dcos(phi),
    z: dsin(phi) * dsin(theta),
  };

  return {
    signalPlanetIndex: chosen.index,
    signalSurfaceDir: surfaceDir,
  };
}

/**
 * Mark antenna ruins as interactable listening posts for Signal Trace mode.
 */
export function makeRuinsInteractable(
  ruins: RuinState[],
  config: ModeConfig
): RuinState[] {
  const activationTurns = config.postActivationTurns ?? DEFAULT_POST_ACTIVATION_TURNS;

  return ruins.map(r => {
    if (r.ruinType === 'antenna' || r.ruinType === 'commsRelay') {
      return {
        ...r,
        interactable: true,
        interactTurns: activationTurns,
        interactEffect: 'listeningPost',
      };
    }
    return r;
  });
}
